import { track } from 'track'
import { ReasonType } from '@/state/reducers'
import { disableAiFeatures } from './aiFeatureStatus'

export interface GenerateReasonsRequest {
  optionLabel: string
  optionId: string
  reasons?: ReasonType[]
  language?: string
}

export interface GenerateReasonsResponse {
  reasons: Pick<ReasonType, 'label' | 'side'>[]
  safetyIdentifier?: string // Returned in dev mode only for debugging
  clientIp?: string // Returned in dev mode only for debugging
}

export interface GenerateReasonsError {
  error: string
  failureKind?: 'invalid_request' | 'feature_disabled' | 'generation_failed'
  referenceId?: string
}

/**
 * Calls the Netlify generateReasons function to generate AI-powered pros and cons
 * @param request - The request payload containing option details
 * @returns Promise with generated reasons or throws an error
 */
export async function generateReasons(
  request: GenerateReasonsRequest,
): Promise<GenerateReasonsResponse> {
  let response: Response
  try {
    response = await fetch('/.netlify/functions/generateReasons', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    })
  } catch (_networkError) {
    // Handle network errors (e.g., no internet, CORS, etc.)
    track('event', 'ai-generate-reasons-api-error', {
      error_type: 'network_error',
      error_message: 'Unable to generate reasons',
    })
    throw new Error('Unable to generate reasons')
  }

  // Check if we got HTML instead of JSON (function not available)
  const contentType = response.headers.get('content-type')
  if (!contentType || !contentType.includes('application/json')) {
    track('event', 'ai-generate-reasons-api-error', {
      error_type: 'invalid_content_type',
      error_message: 'Unable to generate reasons',
      content_type: contentType || 'null',
    })
    throw new Error('Unable to generate reasons')
  }

  let data: GenerateReasonsResponse | GenerateReasonsError
  try {
    data = await response.json()
  } catch (_parseError) {
    // If response is not JSON, it's likely a server error
    track('event', 'ai-generate-reasons-api-error', {
      error_type: 'json_parse_error',
      error_message: 'Unable to generate reasons',
    })
    throw new Error('Unable to generate reasons')
  }

  if (!response.ok) {
    const error = data as GenerateReasonsError
    // Disable AI features for this user to prevent repeated failed requests
    disableAiFeatures()

    // Track the error
    track('event', 'ai-generate-reasons-api-error', {
      error_type: 'response_not_ok',
      error_message: 'Unable to generate reasons',
      failure_kind: error.failureKind || 'unknown',
      status_code: response.status,
    })

    // Never surface backend or upstream failure text to the user.
    throw new Error('Unable to generate reasons')
  }

  return data as GenerateReasonsResponse
}
